import { useCallback, useEffect, useMemo, useState } from 'react';

import { debounce } from '@src/utils';

import { IAttendee, IUIFilters, IWebinarAttendees } from '@src/webinar/types';

import { useWebinarAttendeesQuery, useFilteredWebinarAttendees } from '@src/hooks/queries/useWebinarQuery';

import { ICountry, ICountryCode, TSelectOption } from '@src/types';

export const useAttendeesFilter = (id?: string) => {
  const { data = {} as IWebinarAttendees, isLoading, isError } = useWebinarAttendeesQuery(id);
  const { data: filtersOptions = {} as any } = useFilteredWebinarAttendees(id);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<IUIFilters>({
    types: [],
    emailDomains: [],
    countries: [],
    companies: [],
    search: '',
  });
  const [draftFilters, setDraftFilters] = useState<IUIFilters>(filters);
  const [isBulkActionOpen, setIsBulkActionOpen] = useState<boolean>(false);
  const [selectedAttendees, setSelectedAttendees] = useState<IAttendee[]>([]);

  const [searchValue, setSearchValue] = useState(filters.search);

  const typeOptions = useMemo(
    () => filtersOptions?.TYPES?.map(({ key, value }: TSelectOption<string>) => ({ label: value, value: key })) || [],
    [filtersOptions]
  );

  const emailDomainOptions = useMemo(
    () => filtersOptions?.DOMAIN?.map(({ key, value }: TSelectOption<string>) => ({ label: value, value: key })) || [],
    [filtersOptions]
  );

  const companyOptions = useMemo(
    () => filtersOptions?.COMPANY?.map(({ key, value }: TSelectOption<string>) => ({ label: value, value: key })) || [],
    [filtersOptions]
  );

  const countries = useMemo(
    () =>
      filtersOptions?.COUNTRY?.map(
        ({ key, value, attributes }: ICountryCode) =>
          ({
            label: value,
            value: key,
            code: attributes?.countryCode ?? '',
          }) as ICountry
      ) || [],
    [filtersOptions]
  );

  const handleDraftFilterChange = (updates: Partial<IUIFilters>) => {
    setDraftFilters((prev) => ({ ...prev, ...updates }));
  };
  useEffect(() => {
    if (isFilterModalOpen) {
      setDraftFilters(draftFilters); // preload modal filters with current values
    }
  }, [isFilterModalOpen, draftFilters]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setFilters((prev: IUIFilters) => ({ ...prev, search: value }));
      setSelectedAttendees([]);
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  return {
    loading: isLoading,
    error: isError,
    data,
    cardInfoData: data,

    filters,
    draftFilters,
    isFilterModalOpen,

    countries,
    typeOptions,
    companyOptions,
    emailDomainOptions,

    setFilters,
    setDraftFilters,
    setIsFilterModalOpen,
    handleDraftFilterChange,

    searchValue,
    handleSearchChange,

    selectedAttendees,
    isBulkActionOpen,
    setIsBulkActionOpen,
  };
};
